import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography, Paper } from '@mui/material';
import dayjs from 'dayjs';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
  DatePickerUI,
  ButtonUI,
} from '../Interface';

import useParam from '../../context/ParamContext';

const EditToolbar = (props) => {
  const { params } = useParam();
  const { disabled, push, defaultBatchLine, autocompleteData, apiRef, selectedLineIndex } = props;
  const { values, validateField, setFieldValue } = useFormikContext();

  const handleClick = async () => {
    defaultBatchLine.id = uuidv4();
    await push(defaultBatchLine);
    apiRef.current.startRowEditMode({ id: defaultBatchLine.id });

    const fieldsToValidate = [
      `docLine.${selectedLineIndex}.docLineBatchSn.${values.docLine[selectedLineIndex]?.docLineBatchSn?.length ?? 0}.batchSn`,
      `docLine.${selectedLineIndex}.docLineBatchSn.${values.docLine[selectedLineIndex]?.docLineBatchSn?.length ?? 0}.quantity`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const BatchIn = ({ disabled, autocompleteData, selectedLine, defaultBatchLine, onClose }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  if (!selectedLine || typeof selectedLine.selectedLineIndex !== 'number') return null;
  const { selectedLineIndex } = selectedLine;

  const getBatchIndex = (docLineBatchSn, id) => {
    return docLineBatchSn.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'batchSn',
        headerName: 'Sarzs',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchSn`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.batchSn} />
              }
              onChange={(e) => {
                setFieldValue(
                  `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchSn`,
                  e.target.value ?? ''
                );
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchSn`)
              }
            />
          );
        },
      },
      {
        field: 'quantity',
        headerName: 'Mennyiség',
        type: 'number',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.quantity} />
              }
              onChange={(e) => {
                setFieldValue(
                  `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`,
                  e.target.value ?? ''
                );
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.quantity`)
              }
            />
          );
        },
      },
      {
        field: 'lot',
        headerName: 'LOT',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.lot`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.lot} />
              }
              onChange={(e) => {
                setFieldValue(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.lot`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.lot`)}
            />
          );
        },
      },
      {
        field: 'supplierId',
        headerName: 'Beszállító azon.',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.supplierId`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.supplierId} />
              }
              onChange={(e) => {
                setFieldValue(
                  `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.supplierId`,
                  e.target.value ?? ''
                );
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.supplierId`)
              }
            />
          );
        },
      },
      {
        field: 'expDate',
        headerName: 'Lejárat',
        type: 'date',
        editable: true,
        sortable: false,

        valueGetter: (_data, row) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, row.id);

          return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate
            ? new Date(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate)
            : null;
        },
        renderCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.row.id);
          return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate
            ? dayjs(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate).format('YYYY.MM.DD')
            : '';
        },
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.expDate`}
              type="date"
              variant="standard"
              component={DatePickerUI}
              fullWidth
              value={dayjs(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate)}
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.expDate} />
              }
              onChange={(e) => {
                const newValue = e ? dayjs(e).startOf('day').add(12, 'hours').toISOString() : null;
                setFieldValue(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.expDate`, newValue);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e ? new Date(e) : null,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.expDate`)
              }
            />
          );
        },
      },
      {
        field: 'mnfDate',
        headerName: 'Gyártás',
        type: 'date',
        editable: true,
        sortable: false,
        valueGetter: (_data, row) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, row.id);

          return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate
            ? new Date(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate)
            : null;
        },
        renderCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.row.id);
          return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate
            ? dayjs(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate).format('YYYY.MM.DD')
            : '';
        },
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.mnfDate`}
              type="date"
              variant="standard"
              component={DatePickerUI}
              fullWidth
              value={dayjs(values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate)}
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.mnfDate} />
              }
              onChange={(e) => {
                const newValue = e ? dayjs(e).startOf('day').add(12, 'hours').toISOString() : null;
                setFieldValue(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.mnfDate`, newValue);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e ? new Date(e) : null,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.mnfDate`)
              }
            />
          );
        },
      },
      {
        field: 'batchAttribute1',
        headerName: 'Attribútum 1',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute1`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI
                  message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.batchAttribute1}
                />
              }
              onChange={(e) => {
                setFieldValue(
                  `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute1`,
                  e.target.value ?? ''
                );
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute1`)
              }
            />
          );
        },
      },
      {
        field: 'batchAttribute2',
        headerName: 'Attribútum 2',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute2`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI
                  message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.batchAttribute2}
                />
              }
              onChange={(e) => {
                setFieldValue(
                  `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute2`,
                  e.target.value ?? ''
                );
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.batchAttribute2`)
              }
            />
          );
        },
      },
      {
        field: 'comment',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.comment`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.comment} />
              }
              onChange={(e) => {
                setFieldValue(
                  `docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.comment`,
                  e.target.value ?? ''
                );
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.comment`)
              }
            />
          );
        },
      },
      {
        field: 'price',
        headerName: 'Ár',
        type: 'string',
        editable: true,
        sortable: false,
        valueGetter: (data, row) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, row.id);
          return values.docLine[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.price;
        },
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.price`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.price} />
              }
              onChange={(e) => {
                setFieldValue(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.price`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.price`)
              }
            />
          );
        },
      },
      {
        field: 'other',
        headerName: 'Egyéb',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, data.id);
          return (
            <Field
              name={`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.other`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={
                <FormErrorUI message={errors?.docLine?.[selectedLineIndex]?.docLineBatchSn?.[batchIndex]?.other} />
              }
              onChange={(e) => {
                setFieldValue(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.other`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () =>
                await validateField(`docLine.${selectedLineIndex}.docLineBatchSn.${batchIndex}.other`)
              }
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const batchIndex = getBatchIndex(values.docLine[selectedLineIndex].docLineBatchSn, id);
                return remove(batchIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  const handleSave = () => {
    onClose();
  };

  const handleCancel = () => {
    setFieldValue(`docLine.${selectedLine.selectedLineIndex}.docLineBatchSn`, selectedLine.docLineBatchSn || []);
    onClose();
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box className="align-middle">
        <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
          <Box display="flex" gap={2} p={2} alignItems="center" bgcolor="#f5f5f5" mb={2}>
            <Box flex={0.35} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Sor: </Typography>
              <Typography>{selectedLineIndex + 1}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Cikkszám: </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.itemCode ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Nyitott mennyiség: </Typography>
              <Typography>{values.docLine[selectedLineIndex]?.qty ?? ''}</Typography>
            </Box>
            <Box flex={1} minWidth={0} display="flex" alignItems="center">
              <Typography fontWeight="bold">Összesített sarzs: </Typography>
              <Typography>
                {(values.docLine[selectedLineIndex]?.docLineBatchSn ?? []).reduce(
                  (sum, batch) => sum + (Number(batch.quantity) || 0),
                  0
                )}
              </Typography>
            </Box>
          </Box>

          <FieldArray name={`docLine.${selectedLineIndex}.docLineBatchSn`}>
            {({ remove, push }) => (
              <>
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.BatchTable}
                  height={480}
                  columns={columns(setFieldValue, validateField, errors, remove)}
                  rows={values.docLine[selectedLineIndex]?.docLineBatchSn ?? []}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultBatchLine,
                      autocompleteData,
                      apiRef,
                      selectedLineIndex,
                    },
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    if (disabled) return;
                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                />
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <ButtonUI text="Mentés" className="bg-success" onClick={handleSave} />
                  <ButtonUI text="Mégse" color="blue-gray" variant="gradient" onClick={handleCancel} />
                </Box>
              </>
            )}
          </FieldArray>
        </Paper>
      </Box>
    </Box>
  );
};

export default BatchIn;
